import { Injectable } from '@angular/core';
import { Logger } from '@se/se.ui.html5';
import { BehaviorSubject, noop, Subject } from 'rxjs';

import { AuthData } from './auth-data';
import { IResponseStatus } from '../services/report-workflow-exam.service';

type ConfirmCallback<T> = (authData: AuthData, otherAuthData?: AuthData[]) => Promise<T>;

const defaultConfirmCallback: ConfirmCallback<void> = () => Promise.reject(new CancellationError());

type ConfirmationSuccessArgs =
    | undefined
    | {
          operationCount: number;
          operationFailedCount: number;
      };

export interface ConfirmationFlowOptions<T> {
    onConfirmCallback: ConfirmCallback<T>;
    successMessageKey?: string;
    errorMessageKey?: string;
    partialMessageKey?: string;
    skipSuccessDialog?: boolean;
}
export interface SingleConfirmationFlowOptions<T> extends ConfirmationFlowOptions<T> {
    userId?: string;
}

export interface MultiConfirmationFlowOptions<T> extends ConfirmationFlowOptions<T> {
    userIds: string[];
}

export class CancellationError extends Error {
    constructor(public payload?: any) {
        super('Operation was cancelled by the user.');
    }
}

@Injectable({
    providedIn: 'root',
})
export class ConfirmDialogService {
    // event
    openMultiConfirmWindowRequired = new Subject<string[]>();
    openConfirmWindowRequired = new Subject<string>(); //sottoscritto da confirm-dialog.component @TODO passare un'array di stringhe rappresentanti gli userId cosi da generalizzare per il multi firma/approve
    notifySuccess = new Subject<ConfirmationSuccessArgs | boolean>();
    notifyFailure = new Subject<string>();
    notifyMultiFailure: Subject<IResponseStatus[]> = new Subject<IResponseStatus[]>();

    isWaiting = new BehaviorSubject<boolean>(false);

    successMessageKey: string = '';
    errorMessageKey: string = '';
    partialMessageKey: string = '';
    skipSuccessDialog = true;

    private isRunning = false;
    private onSuccess: (result: unknown) => void = noop;
    private onFailure: (err: Error) => void = noop;
    private confirmCallback: ConfirmCallback<unknown> = defaultConfirmCallback;

    constructor(private logger: Logger) {}

    // public functions
    async beginMultiConfirmationFlow<T>(options: MultiConfirmationFlowOptions<T>) {
        // Non fare nulla se è già aperta una modale.
        if (this.isRunning) {
            return;
        }
        // Inizializzo le callback che mi passa il modulo che richiede la firma
        this.confirmCallback = options.onConfirmCallback;
        this.successMessageKey = options.successMessageKey ?? 'SuccessOperation';
        this.errorMessageKey = options.errorMessageKey ?? 'Exception_CredentialsFault';
        this.partialMessageKey = options.partialMessageKey ?? 'PartiallySuccessOpsOnTotal';
        this.skipSuccessDialog = options.skipSuccessDialog ?? true;

        this.isRunning = true;
        return new Promise<T>((resolve, reject) => {
            this.openMultiConfirmWindowRequired.next(options.userIds);
            this.onSuccess = resolve;
            this.onFailure = reject;
        }).finally(() => (this.isRunning = false));
    }

    /**
     * usata tra le altre per approvare e firmare esami
     * la onConfirmCallback specializza il comportamento
     * @param onConfirmCallback
     * @param userId
     * @param successMessageKey
     * @param errorMessageKey
     * @param partialMessageKey
     */
    beginConfirmationFlow<T>(options: SingleConfirmationFlowOptions<T>) {
        // Non fare nulla se è già aperta una modale.
        if (this.isRunning) {
            return;
        }
        // Inizializzo le callback che mi passa il modulo che richiede la firma
        this.confirmCallback = options.onConfirmCallback;
        this.successMessageKey = options.successMessageKey ?? 'SuccessOperation';
        this.errorMessageKey = options.errorMessageKey ?? 'Exception_CredentialsFault';
        this.partialMessageKey = options.partialMessageKey ?? 'PartiallySuccessOpsOnTotal';

        this.isRunning = true;
        return new Promise<T>((resolve, reject) => {
            this.openConfirmWindowRequired.next(options.userId || '');
            this.onSuccess = resolve;
            this.onFailure = reject;
        }).finally(() => (this.isRunning = false));
    }

    safeExit() {
        // Resetto le callback
        this.confirmCallback = defaultConfirmCallback;
        this.onFailure = noop;
        this.onSuccess = noop;
        this.isRunning = false;

        this.successMessageKey = 'SuccessOperation';
        this.errorMessageKey = 'Exception_CredentialsFault';
        this.partialMessageKey = 'PartiallySuccessOpsOnTotal';
    }

    cancelConfirmFlow() {
        return this.onFailure(new CancellationError());
    }

    async endConfirmFlow(authData: AuthData) {
        this.isWaiting.next(true);
        // Autenticazione
        return this.confirmCallback(authData)
            .then((result) => {
                this.notifySuccess.next(this.skipSuccessDialog);
                this.onSuccess(result);
            })
            .catch((e) => {
                if (e && e.notificationType && e.notificationType == 'warn') {
                    this.notifySuccess.next({
                        operationFailedCount: e.operationFailedCount,
                        operationCount: e.operationCount,
                    });
                } else {
                    const msg = e && e.message ? e.message : '';
                    this.notifyFailure.next(msg);
                }
            })
            .finally(() => {
                this.isWaiting.next(false);
            });
    }

    async endMultiConfirmFlow(authData: AuthData[]) {
        this.isWaiting.next(true);

        try {
            const response = await this.confirmCallback(authData[0], authData.slice(1));
            this.notifySuccess.next(this.skipSuccessDialog);
            this.onSuccess(response);
            return response;
        } catch (e: any) {
            if (e && e.notificationType && e.notificationType === 'warn') {
                this.notifySuccess.next({
                    operationFailedCount: e.operationFailedCount,
                    operationCount: e.operationCount,
                });
            } else {
                try {
                    const msg = e && e.message ? e.message : '[]';
                    this.notifyMultiFailure.next(JSON.parse(msg));
                } catch (parseError) {
                    this.logger.error(parseError);
                }
            }
        } finally {
            this.isWaiting.next(false);
        }
    }
}
